import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Search } from "lucide-react";
import Nav from "@/components/windie/Nav";
import RegistryCard from "@/components/windie/RegistryCard";
import LegalFooter from "@/components/windie/LegalFooter";
import { MCPS, PLUGINS, SKILLS } from "@/lib/registryData";

const TABS = [
  { key: "mcps", label: "MCPs", data: MCPS },
  { key: "plugins", label: "Plugins", data: PLUGINS },
  { key: "skills", label: "Skills", data: SKILLS },
];

function EmptyShelf({ label }) {
  return (
    <div className="rounded-sm border border-dashed border-[#e6e4e0] bg-[#f9f8f6] p-14 text-center">
      <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#828c7e]">
        0 {label}
      </p>
      <h3 className="mt-4 font-serif italic text-3xl sm:text-4xl leading-tight tracking-tight text-[#1c1b1a] font-light">
        The shelf is empty — for now.
      </h3>
      <p className="mt-4 max-w-md mx-auto font-sans text-[15px] leading-relaxed text-[#6b6a68]">
        {label[0].toUpperCase() + label.slice(1)} arrive by invitation. Building
        one? We&apos;d love to see it.
      </p>
      <a
        href="#"
        className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-[#1c1b1a] hover:text-[#828c7e] transition-colors"
      >
        Submit yours <span aria-hidden>↗</span>
      </a>
    </div>
  );
}

export default function Registry() {
  const [active, setActive] = useState("mcps");
  const [query, setQuery] = useState("");
  const activeTab = TABS.find((tab) => tab.key === active);

  const results = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return activeTab.data;

    return activeTab.data.filter((item) =>
      [item.name, item.creator, item.category, item.tagline, ...item.tags]
        .join(" ")
        .toLowerCase()
        .includes(normalized),
    );
  }, [activeTab, query]);

  return (
    <div className="relative min-h-screen bg-[#f9f8f6] text-[#1c1b1a]">
      <Nav />
      <main className="pt-40 sm:pt-44 pb-32">
        <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#6b6a68]"
          >
            — Registry / v0.1
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-6 font-serif text-[13vw] sm:text-[10vw] lg:text-[7.4rem] leading-[0.95] tracking-[-0.02em] text-[#1c1b1a] font-light"
          >
            A curated shelf. <em className="italic">Nothing more.</em>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-8 max-w-xl font-sans text-lg leading-relaxed text-[#6b6a68]"
          >
            Every extension here is tested, credited, and installable in one
            line. Search by name, category, or creator.
          </motion.p>

          <div className="mt-16 flex flex-col md:flex-row md:items-end gap-6 md:gap-10">
            <div className="flex items-center gap-8 border-b border-[#e6e4e0] md:border-b-0">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  data-testid={`registry-tab-${tab.key}`}
                  onClick={() => setActive(tab.key)}
                  className="relative pb-3 font-mono text-[11px] uppercase tracking-[0.22em] text-[#1c1b1a]"
                >
                  <span className={active === tab.key ? "opacity-100" : "opacity-50 hover:opacity-100 transition-opacity"}>
                    {tab.label} <span className="text-[#828c7e]">· {tab.data.length}</span>
                  </span>
                  {active === tab.key && (
                    <motion.span
                      layoutId="registry-page-underline"
                      className="absolute -bottom-px left-0 right-0 h-px bg-[#828c7e]"
                    />
                  )}
                </button>
              ))}
            </div>

            <div className="flex-1 md:max-w-md md:ml-auto">
              <label className="relative block">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6b6a68]" />
                <input
                  data-testid="registry-search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search extensions…"
                  className="w-full rounded-full bg-[#f2efea] border border-[#e6e4e0] focus:border-[#1c1b1a] outline-none pl-10 pr-4 py-3 font-mono text-sm text-[#1c1b1a] placeholder:text-[#6b6a68] transition-colors"
                />
              </label>
            </div>
          </div>

          <div className="mt-14">
            <AnimatePresence mode="wait">
              <motion.div
                key={`${active}-${query}`}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                {results.length === 0 ? (
                  <EmptyShelf label={activeTab.label.toLowerCase()} />
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {results.map((item, index) => (
                      <RegistryCard key={item.slug} item={item} index={index} />
                    ))}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </main>
      <LegalFooter />
    </div>
  );
}
